// Updated: 2026-08-19 13:16 CDT
// Purpose: Define safe author controls and defaults for sentence-level TED-TTS Mode 3 intonation.

import type {
  TedEmotion,
  TedIntonationSpec,
  TedIntonationSpan,
} from "@/types/screenplay";
import { describeDeliveryDirection } from "./pauseDirections";

export const TED_ENGINE_REVISION = "ted-tts-mode3:0.7.0";
export const TED_EMOTIONS: TedEmotion[] = [
  "neutral",
  "angry",
  "happy",
  "sad",
  "fear",
  "disgust",
  "surprise",
];

export const TED_EMOTION_DESCRIPTIONS: Record<TedEmotion, string> = {
  neutral: "neutral: calm, natural, steady rhythm and conversational volume",
  angry: "angry: tense, forceful, sharper articulation with controlled intensity",
  happy: "happy: warm, bright, energetic and lightly upbeat",
  sad: "sad: subdued, slower, softer and emotionally restrained",
  fear: "afraid: controlled tension, alertness, uncertainty and a slightly breathless edge",
  disgust: "disgusted: clear aversion, skepticism and a restrained downward inflection",
  surprise: "surprised: alert, brighter, slightly faster with a rising expressive tone",
};

export const TED_EMOTION_COLORS: Record<TedEmotion, string> = {
  neutral: "#475569",
  angry: "#dc2626",
  happy: "#b45309",
  sad: "#2563eb",
  fear: "#be185d",
  disgust: "#15803d",
  surprise: "#7c3aed",
};

export interface TedEmotionTextRun {
  text: string;
  emotion?: TedEmotion;
  direction?: string;
  spanId?: string;
}

export const tedEmotionTextRuns = (
  text: string,
  spans: TedIntonationSpan[],
): TedEmotionTextRun[] => {
  const orderedSpans = spans
    .map((span) => ({
      ...span,
      start: Math.max(0, Math.min(text.length, span.start)),
      end: Math.max(0, Math.min(text.length, span.end)),
    }))
    .filter((span) => span.end > span.start)
    .sort((left, right) => left.start - right.start || left.end - right.end);
  const runs: TedEmotionTextRun[] = [];
  let cursor = 0;

  orderedSpans.forEach((span) => {
    if (span.start < cursor) return;
    if (span.start > cursor) runs.push({ text: text.slice(cursor, span.start) });
    runs.push({ text: text.slice(span.start, span.end), emotion: span.emotion, spanId: span.id,
      ...(span.gemini ? { direction: describeDeliveryDirection(span.gemini) } : {}) });
    cursor = span.end;
  });

  if (cursor < text.length) runs.push({ text: text.slice(cursor) });
  return runs.length > 0 ? runs : [{ text }];
};

export const createTedIntonationSpec = (): TedIntonationSpec => ({
  version: 1,
  engine: "ted-mode3",
  engineRevision: TED_ENGINE_REVISION,
  source: "writer",
  sourceEmotion: "neutral",
  method: "max_head",
  useRandom: false,
  spans: [],
});

export const createTedIntonationSpan = (
  values: Pick<TedIntonationSpan, "id" | "start" | "end" | "selectedText"> &
    Partial<Omit<TedIntonationSpan, "id" | "start" | "end" | "selectedText">>,
): TedIntonationSpan => {
  const emotion = values.emotion ?? "neutral";

  return {
    id: values.id,
    start: values.start,
    end: values.end,
    selectedText: values.selectedText,
    applicationMode: values.applicationMode ?? "continuous",
    emotion,
    emotionDescription:
      values.emotionDescription ?? TED_EMOTION_DESCRIPTIONS[emotion],
    emotionStrength: values.emotionStrength ?? 1,
    pitchSemitones: values.pitchSemitones ?? 0,
    volumeDb: values.volumeDb ?? 0,
    pauseBeforeMs: values.pauseBeforeMs ?? 0,
    ...(values.gemini ? { gemini: values.gemini } : {}),
  };
};

export const titleTedEmotion = (emotion: TedEmotion) =>
  emotion === "surprise"
    ? "Surprised"
    : emotion === "fear"
      ? "Afraid"
      : emotion === "disgust"
        ? "Disgusted"
        : emotion.charAt(0).toUpperCase() + emotion.slice(1);

export const titleTedApplicationMode = (
  mode: TedIntonationSpan["applicationMode"],
) => mode === "isolated" ? "Isolated punch" : "Continuous blend";
